import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import materialApi, { F111, F1111 } from '../../../services/materialApi';

export type Mode = 'browse' | 'add' | 'edit' | 'copy';

export default function useMaterialForm() {
  const [mode, setMode] = useState<Mode>('browse');
  const [records, setRecords] = useState<F111[]>([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [loading, setLoading] = useState(false);

  // Form state
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [sampleCode, setSampleCode] = useState('');
  const [spec, setSpec] = useState('');
  const [innerWidth, setInnerWidth] = useState(0);
  const [outerWidth, setOuterWidth] = useState(0);
  const [yardWeight, setYardWeight] = useState(0);
  const [gsm, setGsm] = useState(0);
  const [factoryNo, setFactoryNo] = useState('');
  const [active, setActive] = useState(true);
  const [fabrics, setFabrics] = useState<F1111[]>([]);

  const current = records[currentIdx] || null;
  const isEditing = mode !== 'browse';

  const loadRecords = useCallback(async () => {
    setLoading(true);
    try {
      const res = await materialApi.list(1, 500);
      setRecords(res.data || []);
    } catch { message.error('載入失敗'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadRecords(); }, [loadRecords]);

  const syncFromRecord = useCallback((rec: F111 | null) => {
    if (!rec) { setCode(''); setName(''); setDescription(''); setSampleCode(''); setSpec('');
      setInnerWidth(0); setOuterWidth(0); setYardWeight(0); setGsm(0); setFactoryNo(''); setActive(true); setFabrics([]); return; }
    setCode(rec.f111_01); setName(rec.f111_11); setDescription(rec.f111_12); setSampleCode(rec.f111_13);
    setSpec(rec.f111_21); setInnerWidth(rec.f111_22); setOuterWidth(rec.f111_221);
    setYardWeight(rec.f111_24); setGsm(rec.f111_241); setFactoryNo(rec.f111_26);
    setActive(rec.f111_30); setFabrics(rec.fabric_numbers || []);
  }, []);

  useEffect(() => {
    if (mode === 'browse') {
      syncFromRecord(current);
      // List API doesn't include fabric_numbers — fetch full record for browse
      if (current?.f111_01) {
        materialApi.get(current.f111_01).then(r => {
          setFabrics(r.fabric_numbers || []);
        }).catch(() => {});
      }
    }
  }, [currentIdx, records, mode, current, syncFromRecord]);

  const navFirst = () => setCurrentIdx(0);
  const navPrev = () => setCurrentIdx(i => Math.max(0, i - 1));
  const navNext = () => setCurrentIdx(i => Math.min(records.length - 1, i + 1));
  const navLast = () => setCurrentIdx(records.length - 1);

  const quickSearch = useCallback((kw: string) => {
    const k = kw.toUpperCase();
    const idx = records.findIndex(r => r.f111_01.toUpperCase().startsWith(k));
    if (idx >= 0) setCurrentIdx(idx);
  }, [records]);

  const startAdd = () => { setMode('add'); setCode(''); setName(''); setDescription(''); setSampleCode('');
    setSpec(''); setInnerWidth(0); setOuterWidth(0); setYardWeight(0); setGsm(0); setFactoryNo(''); setActive(true); setFabrics([]); };
  const startEdit = () => { if (!current) return; setMode('edit'); syncFromRecord(current);
    materialApi.get(current.f111_01).then(r => setFabrics(r.fabric_numbers || [])).catch(() => {}); };
  const startCopy = () => { if (!current) return; setMode('copy'); setCode(''); syncFromRecord(current);
    setCode(''); };
  const cancel = () => { setMode('browse'); syncFromRecord(current); };

  const buildPayload = (): Partial<F111> => ({
    f111_01: code, f111_11: name, f111_12: description, f111_13: sampleCode,
    f111_21: spec, f111_22: innerWidth, f111_221: outerWidth,
    f111_24: yardWeight, f111_241: gsm, f111_26: factoryNo, f111_30: active,
    fabric_numbers: fabrics,
  });

  const save = async () => {
    try {
      if (mode === 'add') { await materialApi.create(buildPayload()); message.success('新增成功'); }
      else if (mode === 'edit' && current) { await materialApi.update(current.f111_01, buildPayload()); message.success('修改成功'); }
      else if (mode === 'copy' && current) { await materialApi.copy(current.f111_01, code); message.success('複製成功'); }
      setMode('browse'); await loadRecords();
    } catch (err: any) {
      const errors = err?.response?.data?.errors;
      if (errors && Array.isArray(errors)) errors.forEach((e: any) => message.error(e.message));
      else message.error(err?.response?.data?.error || '操作失敗');
    }
  };

  const doDelete = async () => {
    if (!current) return;
    try { await materialApi.delete(current.f111_01); message.success('刪除成功'); await loadRecords();
      if (currentIdx >= records.length - 1) setCurrentIdx(Math.max(0, records.length - 2));
    } catch { message.error('刪除失敗'); }
  };

  const addFabric = () => setFabrics(prev => [...prev, { f1111_01: code || current?.f111_01 || '', f1111_02: '', f1111_11: '' }]);
  const removeFabric = (idx: number) => setFabrics(prev => prev.filter((_, i) => i !== idx));
  const updateFabric = (idx: number, field: keyof F1111, value: string) =>
    setFabrics(prev => prev.map((d, i) => i === idx ? { ...d, [field]: value } : d));

  return {
    mode, records, currentIdx, current, loading, isEditing,
    code, setCode, name, setName, description, setDescription,
    sampleCode, setSampleCode, spec, setSpec,
    innerWidth, setInnerWidth, outerWidth, setOuterWidth,
    yardWeight, setYardWeight, gsm, setGsm,
    factoryNo, setFactoryNo, active, setActive, fabrics,
    navFirst, navPrev, navNext, navLast, quickSearch,
    startAdd, startEdit, startCopy, cancel, save, doDelete,
    addFabric, removeFabric, updateFabric,
  };
}
